import { z } from "zod";

import { listQuerySchema } from "../common/list-query";
import { paginatedSchema } from "../common/pagination";
import { telemetryProviderSchema } from "../devices/devices";
import { frequencyPlanSchema, networkSyncStateSchema } from "../devices/lorawan";

export const GATEWAY_STATUSES = ["ONLINE", "OFFLINE", "UNKNOWN"] as const;
export const gatewayStatusSchema = z.enum(GATEWAY_STATUSES);
export type GatewayStatus = z.infer<typeof gatewayStatusSchema>;

/**
 * Who paid for the gateway.
 *
 * Modelled because it is the question a price cannot be set without
 * answering. The gateway is the one piece of hardware shared across many
 * sensors, so it sits awkwardly in a per-device price and naturally in a
 * per-site one, and who owns it decides who carries that cost
 * (`docs/SALES_MODEL.md`, ADR-061).
 *
 * PLATFORM covers both a gateway we install for one customer and one we
 * install to cover several: what matters to billing is that we paid for it,
 * not how many tenants it happens to serve.
 */
export const GATEWAY_OWNERSHIPS = ["PLATFORM", "CUSTOMER", "THIRD_PARTY", "UNKNOWN"] as const;
export const gatewayOwnershipSchema = z.enum(GATEWAY_OWNERSHIPS);
export type GatewayOwnership = z.infer<typeof gatewayOwnershipSchema>;

/**
 * Gateway EUI: 16 hex characters, stored uppercase.
 *
 * Same shape and the same leniency as a DevEUI, for the same reason — it is
 * read off a label or copied out of a console, and rejecting the separators
 * people actually paste would be pedantry.
 */
export const gatewayEuiSchema = z
  .string()
  .trim()
  .transform((value) => value.replace(/[\s:-]/g, "").toUpperCase())
  .refine((value) => /^[0-9A-F]{16}$/.test(value), {
    message: "Gateway EUI must be 16 hexadecimal characters.",
  });

/**
 * How long a gateway may stay disconnected before super admins are emailed
 * (ADR-093). Same bounds as an alert rule window (ADR-091).
 */
export const GATEWAY_OFFLINE_ALERT_DEFAULT_SECONDS = 300;
export const GATEWAY_OFFLINE_ALERT_MIN_SECONDS = 60;
export const GATEWAY_OFFLINE_ALERT_MAX_SECONDS = 86_400;

export const gatewayOfflineAlertSecondsSchema = z
  .number()
  .int()
  .min(GATEWAY_OFFLINE_ALERT_MIN_SECONDS)
  .max(GATEWAY_OFFLINE_ALERT_MAX_SECONDS);

export const gatewaySchema = z.object({
  id: z.uuid(),
  eui: z.string(),
  name: z.string(),
  provider: telemetryProviderSchema,

  /**
   * Null means shared platform infrastructure serving more than one tenant,
   * which is the arrangement that makes small sites affordable at all: one
   * gateway amortised across every customer inside its radio footprint.
   */
  organizationId: z.uuid().nullable(),
  organizationName: z.string().nullable(),
  siteId: z.uuid().nullable(),
  siteName: z.string().nullable(),

  status: gatewayStatusSchema,
  lastSeenAt: z.iso.datetime().nullable(),
  /**
   * Start of the current Gateway Server connection, polled from The Things
   * Stack; null while it is not connected or before the first check.
   */
  connectedAt: z.iso.datetime().nullable(),
  /** Null means the Gateway Server has never been asked about this gateway. */
  connectionCheckedAt: z.iso.datetime().nullable(),
  /** First poll that found it disconnected; null while connected. */
  disconnectedAt: z.iso.datetime().nullable(),
  offlineAlertAfterSeconds: z.number().int(),

  /**
   * Gateway id in the Network Server. Null for a gateway typed into the
   * console by hand until reconciliation links it by EUI (ADR-089).
   */
  loraGatewayId: z.string().nullable(),
  frequencyPlan: frequencyPlanSchema.nullable(),
  networkSyncState: networkSyncStateSchema,
  /** Sanitized reason for FAILED or DRIFT. */
  networkSyncError: z.string().nullable(),
  networkSyncedAt: z.iso.datetime().nullable(),

  ownership: gatewayOwnershipSchema,
  installedById: z.uuid().nullable(),
  installedByName: z.string().nullable(),
  installedAt: z.iso.datetime().nullable(),
  notes: z.string().nullable(),

  /** How many devices are configured to be covered by this gateway. */
  deviceCount: z.number().int().nonnegative(),
  /** Of those, how many have ever actually reported through it. */
  verifiedDeviceCount: z.number().int().nonnegative(),

  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export type Gateway = z.infer<typeof gatewaySchema>;

export const createGatewayRequestSchema = z.object({
  eui: gatewayEuiSchema,
  name: z.string().trim().min(2).max(120),
  provider: telemetryProviderSchema.default("THE_THINGS_STACK"),
  /** Defaults to the deployment's plan. */
  frequencyPlan: frequencyPlanSchema.optional(),
  /**
   * Absent means shared platform infrastructure. Deliberately not defaulted
   * to the caller's tenant: a gateway that serves everyone and a gateway that
   * serves one customer are different commercial objects, and guessing would
   * quietly assign hardware to whoever happened to register it.
   */
  organizationId: z.uuid().optional(),
  siteId: z.uuid().optional(),
  ownership: gatewayOwnershipSchema.default("UNKNOWN"),
  installedById: z.uuid().optional(),
  installedAt: z.iso.datetime().optional(),
  notes: z.string().trim().max(2_000).optional(),
  /** Super admin only: they are the ones the alert goes to. */
  offlineAlertAfterSeconds: gatewayOfflineAlertSecondsSchema.optional(),
});

export type CreateGatewayRequest = z.infer<typeof createGatewayRequestSchema>;

export const updateGatewayRequestSchema = z
  .object({
    name: z.string().trim().min(2).max(120),
    /** Refused while a linked device is on a different plan. */
    frequencyPlan: frequencyPlanSchema,
    organizationId: z.uuid().nullable(),
    siteId: z.uuid().nullable(),
    ownership: gatewayOwnershipSchema,
    installedById: z.uuid().nullable(),
    installedAt: z.iso.datetime().nullable(),
    notes: z.string().trim().max(2_000).nullable(),
    /** Super admin only: they are the ones the alert goes to. */
    offlineAlertAfterSeconds: gatewayOfflineAlertSecondsSchema,
  })
  .partial()
  // An empty PATCH is a client bug worth surfacing, not a silent no-op.
  .refine((value) => Object.keys(value).length > 0, {
    message: "Provide at least one field to update.",
  });

export type UpdateGatewayRequest = z.infer<typeof updateGatewayRequestSchema>;

export const listGatewaysQuerySchema = listQuerySchema.extend({
  status: gatewayStatusSchema.optional(),
  ownership: gatewayOwnershipSchema.optional(),
  siteId: z.uuid().optional(),
  /**
   * Shared infrastructure has no organization, so it cannot be reached by
   * narrowing `organizationId`. This flag is the only way to ask for it.
   */
  shared: z.stringbool().optional(),
});

export type ListGatewaysQuery = z.infer<typeof listGatewaysQuerySchema>;

export const gatewayListSchema = paginatedSchema(gatewaySchema);
export type GatewayList = z.infer<typeof gatewayListSchema>;
